import { useEffect, useRef, useState } from 'react';

const AudioSpectrum = ({
    backgroundColor = '#000000',
    smoothing = 0.8,
    defaultMode = 'automatic',
    colorSpeed = 0.5
}) => {
    const canvasRef = useRef(null);
    const [mode, setMode] = useState(defaultMode);
    const [isListening, setIsListening] = useState(false);
    const [error, setError] = useState(null);
    const animationRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const dataArrayRef = useRef(null);
    const timeRef = useRef(0);

    // Helper function to get animated color
    const getAnimatedColor = (index, total, time) => {
        const hue = ((index / total) * 360 + time * colorSpeed * 50) % 360;
        const saturation = 70 + Math.sin(time + index * 0.1) * 30;
        const lightness = 50 + Math.sin(time * 0.5 + index * 0.05) * 20;
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };

    // Automatic mode animation
    useEffect(() => {
        if (mode !== 'automatic') return;
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const animate = () => {
            const width = canvas.width;
            const height = canvas.height;

            // Calculate bar dimensions to fill entire width
            const barCount = Math.floor(width / 6); // 4px bar + 2px gap
            const barWidth = Math.floor(width / barCount) - 1;

            // Clear canvas
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, width, height);

            // Draw bars across full width
            for (let i = 0; i < barCount; i++) {
                const x = i * (barWidth + 1);

                // Create wave patterns
                const wave1 = Math.sin(timeRef.current * 0.5 + i * 0.1) * 0.5 + 0.5;
                const wave2 = Math.sin(timeRef.current * 0.3 + i * 0.15) * 0.5 + 0.5;
                const wave3 = Math.sin(timeRef.current * 0.7 + i * 0.05) * 0.5 + 0.5;

                const barHeight = (wave1 * 0.4 + wave2 * 0.3 + wave3 * 0.3) * height * 0.85;
                const y = height - barHeight;

                // Animated gradient
                const gradient = ctx.createLinearGradient(0, y, 0, height);
                const topColor = getAnimatedColor(i, barCount, timeRef.current);
                gradient.addColorStop(0, topColor);
                gradient.addColorStop(0.5, topColor);
                gradient.addColorStop(1, backgroundColor);

                ctx.fillStyle = gradient;
                ctx.fillRect(x, y, barWidth, barHeight);
            }

            timeRef.current += 0.02;
            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [mode, backgroundColor, colorSpeed]);

    // Audio reactive mode
    useEffect(() => {
        if (mode !== 'audio') return;
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const startAudio = async () => {
            try {
                setError(null);

                // Request microphone access
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaStreamRef.current = stream;

                // Create audio context and analyser
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                audioContextRef.current = audioContext;

                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 512;
                analyser.smoothingTimeConstant = smoothing;
                analyserRef.current = analyser;

                const source = audioContext.createMediaStreamSource(stream);
                source.connect(analyser);

                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);
                dataArrayRef.current = dataArray;

                setIsListening(true);

                const animate = () => {
                    if (mode !== 'audio') return;

                    const width = canvas.width;
                    const height = canvas.height;

                    // Calculate bar dimensions to fill entire width (same as automatic mode)
                    const barCount = Math.floor(width / 6); // Ensures bars fill the whole width
                    const barWidth = Math.floor(width / barCount) - 1;

                    // Get frequency data
                    analyser.getByteFrequencyData(dataArray);

                    // Clear canvas
                    ctx.fillStyle = backgroundColor;
                    ctx.fillRect(0, 0, width, height);

                    // Draw frequency bars across full width
                    for (let i = 0; i < barCount; i++) {
                        const x = i * (barWidth + 1);
                        // Map bar index to frequency data
                        const dataIndex = Math.floor((i / barCount) * bufferLength);
                        const value = dataArray[dataIndex] / 255;
                        const barHeight = value * height * 0.9;
                        const y = height - barHeight;

                        // Animated gradient based on frequency and time
                        const gradient = ctx.createLinearGradient(0, y, 0, height);
                        const color = getAnimatedColor(i, barCount, timeRef.current);
                        const brighterColor = getAnimatedColor(i, barCount, timeRef.current + 0.5);

                        gradient.addColorStop(0, brighterColor);
                        gradient.addColorStop(0.6, color);
                        gradient.addColorStop(1, backgroundColor);

                        ctx.fillStyle = gradient;
                        ctx.fillRect(x, y, barWidth, barHeight);
                    }

                    timeRef.current += 0.02;
                    animationRef.current = requestAnimationFrame(animate);
                };

                animate();
            } catch (err) {
                console.error('Audio access error:', err);
                setError('Microphone access denied. Please allow microphone access to use audio mode.');
                setMode('automatic');
            }
        };

        startAudio();

        return () => {
            window.removeEventListener('resize', resize);

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }

            // Cleanup audio
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
                mediaStreamRef.current = null;
            }

            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }

            setIsListening(false);
        };
    }, [mode, backgroundColor, smoothing, colorSpeed]);

    const toggleMode = () => {
        setMode(prev => prev === 'automatic' ? 'audio' : 'automatic');
    };

    return (
        <div className="relative w-full h-full overflow-hidden" style={{ backgroundColor }}>
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full"
            />

            {/* Toggle Button - Compact Switch */}
            <div className="absolute top-4 right-4 z-50">
                <button
                    onClick={toggleMode}
                    className="relative px-3 py-1.5 rounded-full font-medium text-xs transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2"
                    style={{
                        backgroundColor: mode === 'audio' ? 'rgba(0, 255, 136, 0.2)' : 'rgba(80, 80, 80, 0.2)',
                        border: `2px solid ${mode === 'audio' ? '#00ff88' : '#888'}`,
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    <span className={`transition-all ${mode === 'audio' ? 'text-green-400' : 'text-gray-400'}`}>
                        {mode === 'automatic' ? '🎵' : '✨'}
                    </span>
                    <div
                        className="w-8 h-4 rounded-full relative transition-all duration-300"
                        style={{
                            backgroundColor: mode === 'audio' ? '#00ff88' : '#555'
                        }}
                    >
                        <div
                            className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-md transition-all duration-300"
                            style={{
                                left: mode === 'audio' ? '18px' : '2px'
                            }}
                        />
                    </div>
                </button>
            </div>

            {/* Status Indicator */}
            {mode === 'audio' && (
                <div className="absolute top-4 left-4 z-50 pointer-events-none">
                    <div className={`px-4 py-2 rounded-full text-sm font-bold backdrop-blur-md shadow-lg ${isListening ? 'bg-green-500 text-black' : 'bg-red-500 text-white'
                        }`}>
                        {isListening ? '🎤 Listening' : '🔇 No Audio'}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 max-w-md">
                    <div className="bg-red-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm shadow-lg">
                        {error}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudioSpectrum;
